/**
 * SiftConditionBuilderCompact
 *
 * Narrow-width-friendly condition builder used inside the contact-detail
 * cadence card. The wider `SiftConditionBuilder` lays out
 * [field][operator][value][delete] as a horizontal row that clips on the
 * sidebar — this version stacks the three fields vertically inside a card
 * with the delete X in the corner so every input gets full width.
 */
import { useCallback, useMemo } from 'react';
import { nanoid } from 'nanoid';
import { cn } from '../cn';
import { Button, Input, Select, Textarea } from '../Input2';
import { General } from '../StrokeIcons';

import type {
  SiftCondition,
  SiftConfig,
  SiftFieldName,
  SiftOperator,
} from '@poseidon/types/types/sift.type';
import {
  SIFT_FIELD_OPTIONS,
  SIFT_OPERATOR_LABELS,
  getOperatorsForField,
  operatorRequiresValue,
} from '@poseidon/types/types/sift.type';

interface SiftConditionBuilderCompactProps {
  value: SiftConfig;
  onChange: (config: SiftConfig) => void;
  className?: string;
}

const fieldOptions = SIFT_FIELD_OPTIONS.map((f) => ({ value: f.field, label: f.label }));

function getOperatorOptions(field: SiftFieldName) {
  return getOperatorsForField(field).map((op) => ({
    value: op,
    label: SIFT_OPERATOR_LABELS[op],
  }));
}

export default function SiftConditionBuilderCompact({
  value,
  onChange,
  className,
}: SiftConditionBuilderCompactProps) {
  const conditions = value.conditions;

  const addCondition = useCallback(() => {
    const newCondition: SiftCondition = {
      id: nanoid(),
      field: 'title',
      operator: 'contains',
      value: '',
    };
    onChange({ conditions: [...conditions, newCondition] });
  }, [conditions, onChange]);

  const removeCondition = useCallback(
    (id: string) => {
      onChange({ conditions: conditions.filter((c) => c.id !== id) });
    },
    [conditions, onChange],
  );

  const updateCondition = useCallback(
    (id: string, updates: Partial<SiftCondition>) => {
      onChange({
        conditions: conditions.map((c) => (c.id === id ? { ...c, ...updates } : c)),
      });
    },
    [conditions, onChange],
  );

  return (
    <div
      data-testid="sift-builder-compact"
      className={cn('flex w-full flex-col gap-2', className)}>
      <p className="text-xs text-lynch-700 dark:text-lynch-300">
        Contact must match <span className="font-semibold">ALL</span> conditions to proceed
      </p>

      {conditions.length === 0 && (
        <div
          data-testid="sift-builder-compact-empty"
          className="rounded-md border border-dashed border-lynch-300 px-3 py-3 text-center text-xs italic text-lynch-600 dark:border-slate-700 dark:text-lynch-400">
          No conditions yet. Click <span className="font-semibold">Add condition</span> to filter contacts.
        </div>
      )}

      {conditions.map((condition, idx) => (
        <CompactConditionCard
          key={condition.id}
          index={idx}
          condition={condition}
          onUpdate={(updates) => updateCondition(condition.id, updates)}
          onRemove={() => removeCondition(condition.id)}
        />
      ))}

      <Button
        data-testid="sift-builder-compact-add"
        type="button"
        color="blue" tone="soft"
        size="sm"
        onClick={addCondition}
        icon={<General.Plus />}
        textAlign="center"
        className="w-full justify-center">
        {conditions.length === 0 ? 'Add a condition' : 'Add another condition'}
      </Button>
    </div>
  );
}

// String-matching operators where the comparison is case-insensitive on the
// server. Used by the value input to surface a small hint when relevant.
const CASE_INSENSITIVE_STRING_OPERATORS: ReadonlySet<SiftOperator> = new Set<SiftOperator>([
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'in_list',
  'not_in_list',
]);

interface CompactConditionCardProps {
  condition: SiftCondition;
  index: number;
  onUpdate: (updates: Partial<SiftCondition>) => void;
  onRemove: () => void;
}

function CompactConditionCard({ condition, index, onUpdate, onRemove }: CompactConditionCardProps) {
  const operatorOptions = useMemo(
    () => getOperatorOptions(condition.field),
    [condition.field],
  );

  const fieldMeta = SIFT_FIELD_OPTIONS.find((f) => f.field === condition.field);
  const showValueInput = operatorRequiresValue(condition.operator);
  const isBooleanField = fieldMeta?.type === 'boolean';
  const isNumberField = fieldMeta?.type === 'number';
  const isListOperator =
    condition.operator === 'in_list' || condition.operator === 'not_in_list';

  const handleFieldChange = useCallback(
    (newField: SiftFieldName) => {
      const newOperators = getOperatorsForField(newField);
      const currentOperatorValid = newOperators.includes(condition.operator);
      const newFieldMeta = SIFT_FIELD_OPTIONS.find((f) => f.field === newField);
      onUpdate({
        field: newField,
        operator: currentOperatorValid ? condition.operator : newOperators[0],
        value: newFieldMeta?.type === 'boolean' ? 'true' : '',
      });
    },
    [condition.operator, onUpdate],
  );

  return (
    <div
      data-testid="sift-builder-compact-condition"
      data-condition-index={index}
      className="relative flex flex-col gap-1.5 rounded-md border border-lynch-200 bg-white p-2 pr-8 dark:border-slate-700 dark:bg-slate-900">
      <Button
        data-testid="sift-builder-compact-remove"
        type="button"
        color="red" tone="soft"
        size="2xs"
        onClick={onRemove}
        icon={<General.XMark />}
        tooltip="Remove condition"
        className="absolute right-1 top-1"
      />

      <FieldLabel>Field</FieldLabel>
      <Select
        value={condition.field}
        onChange={(e) => handleFieldChange(e.target.value as SiftFieldName)}
        options={fieldOptions}
        className="w-full"
      />

      <FieldLabel>Condition</FieldLabel>
      <Select
        value={condition.operator}
        onChange={(e) => onUpdate({ operator: e.target.value as SiftOperator })}
        options={operatorOptions}
        className="w-full"
      />

      {showValueInput && (
        <>
          <FieldLabel>Value</FieldLabel>
          {!isBooleanField && !isNumberField && !isListOperator && (
            <Input
              value={condition.value}
              onChange={(e) => onUpdate({ value: e.target.value })}
              placeholder="Value…"
              className="w-full"
            />
          )}
          {isNumberField && (
            <Input
              type="number"
              value={condition.value}
              onChange={(e) => onUpdate({ value: e.target.value })}
              placeholder="Enter number…"
              className="w-full"
            />
          )}
          {isBooleanField && (
            <Select
              value={condition.value}
              onChange={(e) => onUpdate({ value: e.target.value })}
              options={[
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' },
              ]}
              className="w-full"
            />
          )}
          {isListOperator && (
            <Textarea
              value={condition.value}
              onChange={(e) => onUpdate({ value: e.target.value })}
              placeholder="One value per line, or comma-separated…"
              className="w-full"
              rows={2}
            />
          )}
          {!isBooleanField &&
            !isNumberField &&
            CASE_INSENSITIVE_STRING_OPERATORS.has(condition.operator) && (
              <span
                data-testid="sift-builder-compact-case-hint"
                className="text-[10px] italic text-lynch-500 dark:text-lynch-400">
                Matching is case-insensitive — “CTO” and “cto” match the same contacts.
              </span>
            )}
        </>
      )}
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="select-none text-[10px] font-semibold uppercase tracking-wide text-lynch-500 dark:text-lynch-400">
      {children}
    </span>
  );
}
